import axios from "axios";
import { readToken } from "./session";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/* ------------------------------------------------------------------
   Session-only provider key.

   When the user declines to store their key, it lives here: in the tab,
   for as long as the tab is open, and it is attached to each request as a
   header. It is never written to localStorage, so it does not survive a
   restart and cannot be read back by a later session.
------------------------------------------------------------------- */

const SESSION_KEY = "numa_llm_session";

export const sessionCredentials = {
  get() {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },
  set(creds) {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(creds));
    } catch {
      /* private browsing: the key simply won't persist across reloads */
    }
  },
  clear() {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
  },
};

function llmHeaders() {
  const creds = sessionCredentials.get();
  if (!creds?.api_key) return {};
  const headers = {
    "X-LLM-Key": creds.api_key,
    "X-LLM-Provider": creds.provider,
  };
  if (creds.model) headers["X-LLM-Model"] = creds.model;
  if (creds.base_url) headers["X-LLM-Base-Url"] = creds.base_url;
  return headers;
}

export const api = axios.create({
  baseURL: API,
  withCredentials: false,
  timeout: 180000,
});

api.interceptors.request.use((config) => {
  const token = readToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  Object.entries(llmHeaders()).forEach(([k, v]) => {
    config.headers[k] = v;
  });
  return config;
});

/* ------------------------------------------------------------------
   Error normalisation.

   The backend answers LLM failures with a structured body. Everything in
   the UI reads errors through this one function, so a missing key, an empty
   balance and a rate limit are never collapsed into "something went wrong".
------------------------------------------------------------------- */

export const LLM_CODES = {
  NO_KEY: "no_key",
  INVALID_KEY: "invalid_key",
  NO_CREDIT: "no_credit",
  RATE_LIMIT: "rate_limit",
  CONTEXT_TOO_LONG: "context_too_long",
  MODEL_NOT_FOUND: "model_not_found",
};

export function normalizeError(err) {
  /*
    Already normalised? Pass it through.

    This has to be decided on more than the presence of `code`: axios puts its
    own code on every failure ("ERR_BAD_REQUEST"), so a raw axios error looks
    normalised, and treating it as such is what surfaced "Request failed with
    status code 428" instead of the explanation the server had sent.
  */
  if (err && !err.response && !err.isAxiosError && err.code && err.message && !err.stack) {
    return err;
  }
  /*
    Un guasto di rete ha un nome, e non è «Network Error».

    Axios scrive quella stringa in inglese quando la richiesta non arriva a
    destinazione - il server sta ripartendo, la rete ha avuto un singhiozzo,
    il portatile è passato da una wifi all'altra - e finiva a schermo tale e
    quale, dentro una finestra rossa che sembrava un guasto di NUMA. Qui
    diventa un codice che l'interfaccia sa spiegare in italiano.
  */
  const senzaRisposta = Boolean(err) && !err.response && (err.isAxiosError || err.code);
  if (senzaRisposta) {
    const scaduta = err.code === "ECONNABORTED" || /timeout/i.test(err.message || "");
    if (scaduta) {
      return {
        code: "timeout",
        message: "Il server ci sta mettendo troppo a rispondere.",
        action: "Riprova fra un momento: il lavoro che hai fatto è salvato.",
      };
    }
    if (err.code === "ERR_NETWORK" || /network error/i.test(err.message || "")) {
      return {
        code: "network",
        message: "NUMA non è riuscita a parlare con il server.",
        action: "Controlla la connessione e riprova: quello che hai scritto è al sicuro.",
      };
    }
  }
  const detail = err?.response?.data?.detail ?? err?.detail ?? err;
  if (detail && typeof detail === "object" && detail.code) {
    return {
      code: detail.code,
      message: detail.message || "Errore del provider.",
      action: detail.action || "",
      provider: detail.provider || "",
      raw: detail.detail || "",
    };
  }
  if (typeof detail === "string") return { code: "error", message: detail, action: "" };

  /*
    FastAPI answers a failed validation with 422 and a LIST of problems, not a
    string. Falling through to `err.message` here is what produced the useless
    "Request failed with status code 422" on the registration form, while the
    server had said exactly which field was wrong and why.
  */
  if (Array.isArray(detail) && detail.length) {
    const fields = detail
      .map((item) => {
        const text = String(item?.msg || "").replace(/^Value error,\s*/i, "");
        const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : "";
        if (!text) return "";
        // Pydantic's own English messages are not worth showing to an author.
        if (/valid email/i.test(text)) return "L'indirizzo email non è valido.";
        if (/field required/i.test(text)) return `Manca un dato obbligatorio: ${field}.`;
        return text;
      })
      .filter(Boolean);
    if (fields.length) {
      return { code: "validation", message: fields[0], action: fields.slice(1).join(" "), fields };
    }
  }
  return { code: "error", message: err?.message || "Errore imprevisto.", action: "" };
}

/** True when the failure is something only the user can resolve. */
export function needsUserAction(code) {
  return [LLM_CODES.NO_KEY, LLM_CODES.INVALID_KEY, LLM_CODES.NO_CREDIT].includes(code);
}

/* ---------------------------------------------------------------- auth */

/** Settings the server decides and the interface has to state correctly. */
export const config = {
  get: () => api.get("/config").then((r) => r.data),
};

export const auth = {
  register: (data) => api.post("/auth/register", data).then((r) => r.data),
  login: (data) => api.post("/auth/login", data).then((r) => r.data),
  // Un tetto suo, molto più basso dei tre minuti generali: è la richiesta che
  // sta fra chi apre NUMA e la sua pagina, e tre minuti davanti a un logo che
  // gira non li aspetta nessuno.
  me: () => api.get("/auth/me", { timeout: 20000 }).then((r) => r.data),
  updateProfile: (data) => api.patch("/auth/me", data).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  /** Chiede il link per rimettere la password. */
  forgotPassword: (email) =>
    api.post("/auth/forgot-password", { email }).then((r) => r.data),
  /** Usa quel link una volta sola, per scegliere la password nuova. */
  resetPassword: (token, password) =>
    api.post("/auth/reset-password", { token, password }).then((r) => r.data),
  verifyEmail: (token) => api.post("/auth/verify", { token }).then((r) => r.data),
  resendVerification: () => api.post("/auth/resend-verification").then((r) => r.data),
};

/* ----------------------------------------------------------------- llm */

export const llm = {
  providers: () => api.get("/llm/providers").then((r) => r.data),
  keys: () => api.get("/llm/keys").then((r) => r.data),
  saveKey: (data) => api.post("/llm/keys", data).then((r) => r.data),
  activate: (provider) => api.post(`/llm/keys/${provider}/activate`).then((r) => r.data),
  removeKey: (provider) => api.delete(`/llm/keys/${provider}`).then((r) => r.data),
  status: () => api.get("/llm/status").then((r) => r.data),
  oauthStart: (provider, callback_url, code_challenge) =>
    api.post("/llm/oauth/start", { provider, callback_url, code_challenge }).then((r) => r.data),
  oauthExchange: (provider, code, code_verifier, model) =>
    api.post("/llm/oauth/exchange", { provider, code, code_verifier, model }).then((r) => r.data),
};

/* ------------------------------------------------------------ legale */

export const legal = {
  privacy: () => api.get("/legal/privacy").then((r) => r.data),
  terms: () => api.get("/legal/terms").then((r) => r.data),
  owner: () => api.get("/legal/owner").then((r) => r.data),
  consentStatus: () => api.get("/legal/consent").then((r) => r.data),
  acceptConsent: (privacy_version, terms_version) =>
    api.post("/legal/consent", { privacy_version, terms_version }).then((r) => r.data),
};

export const account = {
  exportData: () => api.get("/account/export").then((r) => r.data),
  remove: () => api.delete("/account").then((r) => r.data),
};

export const support = {
  send: (data) => api.post("/support", data).then((r) => r.data),
};

/* ------------------------------------------------------------ projects */

export const theses = {
  list: () => api.get("/theses").then((r) => r.data),
  get: (id) => api.get(`/theses/${id}`).then((r) => r.data),
  create: (data) => api.post("/theses", data).then((r) => r.data),
  remove: (id) => api.delete(`/theses/${id}`).then((r) => r.data),
  updateChapter: (tid, cid, data) => api.put(`/theses/${tid}/chapters/${cid}`, data).then((r) => r.data),
  createChapter: (tid, data) => api.post(`/theses/${tid}/chapters`, data).then((r) => r.data),
  saveImprovement: (tid, cid, data) => api.post(`/theses/${tid}/chapters/${cid}/improvements`, data).then((r) => r.data),
  history: (tid) => api.get(`/theses/${tid}/history`).then((r) => r.data),
  getVersion: (tid, vid) => api.get(`/theses/${tid}/history/${vid}`).then((r) => r.data),
  restoreVersion: (tid, cid, version_id) => api.post(`/theses/${tid}/chapters/${cid}/restore`, { version_id }).then((r) => r.data),
  restructureChapter: (tid, cid, data) => api.post(`/theses/${tid}/chapters/${cid}/restructure`, data).then((r) => r.data),
  import: (file, title) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title || "Documento importato");
    return api.post("/theses/import", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  /**
   * Detect the structure of an existing document without creating anything.
   *
   * Heading detection is a guess, so the guess is shown and corrected before
   * a project exists; `importConfirm` then builds it from the approved shape.
   */
  setMode: (thesisId, mode) =>
    api.patch(`/theses/${thesisId}/mode`, { mode }).then((r) => r.data),
  /**
   * Legge il documento e restituisce le sezioni riconosciute.
   *
   * Il server prende in carico il file e risponde subito con un numero di
   * pratica; la lettura di trecento pagine dura più di quanto una richiesta
   * HTTP possa restare aperta - il proxy dell'hosting la taglia, ed è quello
   * che faceva comparire «caricamento non riuscito» dopo una lunga attesa.
   * Qui si chiede come procede ogni secondo e mezzo, e `onProgress` riceve
   * quante pagine sono state lette.
   */
  /** Smette di leggere: chi annulla ha spesso solo sbagliato file. */
  importCancel: (jobId) =>
    api.delete(`/theses/import/preview/${jobId}`).then((r) => r.data).catch(() => null),

  importPreview: async (file, onProgress, ancoraValida) => {
    const fd = new FormData();
    fd.append("file", file);
    const presa = await api
      .post("/theses/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
      .then((r) => r.data);
    // Se nel frattempo la finestra è stata chiusa o è stato scelto un altro
    // file, questa lettura non serve più a nessuno: si ferma subito, di qua e
    // di là. Senza, i due avanzamenti si accavallavano a schermo e la macchina
    // continuava a leggere il documento sbagliato.
    const abbandona = async () => {
      await api.delete(`/theses/import/preview/${presa.job_id}`).catch(() => null);
      const fine = new Error("lettura annullata");
      fine.annullata = true;
      throw fine;
    };
    if (ancoraValida && !ancoraValida()) await abbandona();
    onProgress?.({ done: 0, pages: presa.pages || 0 });

    // Nessun limite di tentativi, un limite di tempo: venti minuti bastano a
    // mille pagine sulla macchina più lenta, e oltre è successo qualcosa.
    const scadenza = Date.now() + 20 * 60 * 1000;
    for (;;) {
      await new Promise((r) => setTimeout(r, 1500));
      if (ancoraValida && !ancoraValida()) await abbandona();
      const stato = await api
        .get(`/theses/import/preview/${presa.job_id}`, { timeout: 30000 })
        .then((r) => r.data);
      if (stato.status === "done") return stato;
      if (stato.status === "cancelled") {
        const fine = new Error("lettura annullata");
        fine.annullata = true;
        throw fine;
      }
      onProgress?.({ done: stato.done_pages || 0, pages: stato.pages || presa.pages || 0 });
      if (Date.now() > scadenza) {
        throw new Error("La lettura sta durando troppo: riprova con un file più piccolo.");
      }
    }
  },
  importConfirm: (body) =>
    api.post("/theses/import/confirm", body, { timeout: 300000 }).then((r) => r.data),
};

/* ---------------------------------------------------------------- pdfs */

export const pdfs = {
  list: (tid) => api.get(`/theses/${tid}/pdfs`).then((r) => r.data),
  get: (pid) => api.get(`/pdfs/${pid}`).then((r) => r.data),
  getPages: (pid) => api.get(`/pdfs/${pid}/pages`).then((r) => r.data),
  qa: (pid, question) => api.post(`/pdfs/${pid}/qa`, { question }).then((r) => r.data),
  upload: (tid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/theses/${tid}/pdfs`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  summarize: (pid) => api.post(`/pdfs/${pid}/summarize`).then((r) => r.data),
  /**
   * Transcribe a scanned source.
   *
   * The file is sent again from the browser because NUMA stores the text of a
   * source, not its bytes - keeping every PDF anyone ever uploaded, only so it
   * could be re-read later, is a worse trade than asking for the file again.
   */
  ocr: (pid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post(`/pdfs/${pid}/ocr`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      })
      .then((r) => r.data);
  },
  remove: (pid) => api.delete(`/pdfs/${pid}`).then((r) => r.data),
  generateBibliography: (thesisId, pdfIds = [], format = "APA") =>
    api.post(`/theses/${thesisId}/bibliography`, { pdf_ids: pdfIds, format }).then((r) => r.data),
  formatBibliographyFile: (thesisId, file, format = "APA") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("format", format);
    return api.post(`/theses/${thesisId}/bibliography/format`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
};

/* --------------------------------------------------------------
   Deterministic checks and bibliography.

   None of these need a provider key: they are the part of the product
   that runs on arithmetic and public catalogues, not on a model.
----------------------------------------------------------------- */

export const checks = {
  run: (thesisId) => api.get(`/checks/${thesisId}`).then((r) => r.data),
  grammar: (thesisId, chapterId = "") =>
    api.get(`/checks/${thesisId}/grammar`, { params: { chapter_id: chapterId } }).then((r) => r.data),
  dismiss: (thesisId, finding_id, check, dismissed = true) =>
    api.post(`/checks/${thesisId}/dismiss`, { finding_id, check, dismissed }).then((r) => r.data),
  /** The full reading of a finished manuscript: rules, language, merit. */
  /*
    La revisione, con un numero di pratica per sapere a che punto è.

    La richiesta resta aperta finché il documento non è letto tutto - su
    ventisei sezioni sono minuti - e nel frattempo l'unica cosa che si può dire
    a chi aspetta è quante sezioni sono fatte. Il numero di pratica serve a
    questo: si manda con la richiesta e poi si chiede, ogni mezzo secondo, a
    che punto è quella revisione lì.
  */
  review: (thesisId, { chapterId = "", includeAi = true, progressId = "" } = {}) =>
    api
      .post(`/checks/${thesisId}/review`,
            { chapter_id: chapterId, include_ai: includeAi, progress_id: progressId },
            { timeout: 600000 })
      .then((r) => r.data),

  /**
   * La revisione, chiesta come si chiede una pratica.
   *
   * Prima era una sola richiesta che restava aperta finché il documento non
   * era letto tutto: su trecento pagine erano minuti, e una richiesta di
   * minuti non arriva in fondo - la taglia il browser o il proxy, e quello che
   * si legge a schermo è «network error» dopo una lunga attesa. Adesso la
   * richiesta parte e torna subito con un numero; poi si chiede a che punto è,
   * una domanda piccola ogni secondo e mezzo, finché non c'è il rapporto.
   */
  reviewRun: async (thesisId, { chapterId = "", includeAi = true } = {}, onProgress, ancoraValida) => {
    const avvia = () => api
      .post(`/checks/${thesisId}/review/start`,
            { chapter_id: chapterId, include_ai: includeAi }, { timeout: 60000 })
      .then((r) => r.data);

    let presa = await avvia();
    onProgress?.({ fase: "controlli", fatte: 0, totale: presa.chapters_total || 0 });
    /*
      Una pratica che il server non riconosce più si rifà, non si annuncia.

      Le pratiche stanno nella memoria del server: se il server viene
      riavviato - un aggiornamento, un riavvio dell'hosting - quella in corso
      sparisce. Prima l'autore leggeva «la revisione si è interrotta» e doveva
      ricominciare a mano; ma ricominciare è esattamente ciò che si può fare da
      soli, e costa pochi secondi. Si riprova una volta: se sparisce anche la
      seconda, allora è successo qualcosa che vale la pena di raccontare.
    */
    let riavviata = false;

    // Un limite di tempo, non di tentativi: un quarto d'ora basta a mille
    // pagine sulla macchina più lenta, e oltre è successo qualcosa d'altro.
    const scadenza = Date.now() + 15 * 60 * 1000;
    const partito = Date.now();
    let muti = 0;   // sondaggi andati a vuoto di fila
    for (;;) {
      // Si chiede spesso all'inizio e più di rado dopo: su una revisione di un
      // minuto e mezzo, domandare ogni secondo e mezzo per tutto il tempo sono
      // sessanta domande, e dalla decima in poi non dicono niente di nuovo.
      const attesa = Math.min(4000, 1200 + (Date.now() - partito) / 15);
      await new Promise((r) => setTimeout(r, attesa));
      if (ancoraValida && !ancoraValida()) {
        const fine = new Error("revisione abbandonata");
        fine.annullata = true;
        throw fine;
      }
      /*
        Un sondaggio che non arriva non è una revisione fallita.

        La revisione sta lavorando sul server, e mentre lavora il server è
        occupato: una domanda ogni tanto può andare persa, o scadere. Prima
        bastava una di queste a far comparire una finestra rossa con scritto
        «Network Error» addosso a un lavoro che stava andando benissimo. Si
        riprova, e solo dopo parecchi silenzi di fila si dice che qualcosa non
        va davvero.
      */
      let stato;
      try {
        stato = await api
          .get(`/checks/${thesisId}/review/job/${presa.job_id}`, { timeout: 30000 })
          .then((r) => r.data);
        muti = 0;
      } catch (guasto) {
        muti += 1;
        if (guasto?.response) throw guasto;          // il server ha risposto: è un errore vero
        if (muti >= 6) throw guasto;
        continue;
      }
      if (stato.state === "done") return stato.report;
      if (stato.state === "unknown") {
        if (riavviata) {
          throw new Error("La revisione si è interrotta due volte di seguito.");
        }
        riavviata = true;
        muti = 0;
        presa = await avvia();
        continue;
      }
      onProgress?.(stato);
      if (Date.now() > scadenza) {
        throw new Error("La revisione sta durando troppo: prova una sezione alla volta.");
      }
    }
  },
  reviewProgress: (thesisId, progressId) =>
    api
      .get(`/checks/${thesisId}/review/progress/${progressId}`, { timeout: 15000 })
      .then((r) => r.data)
      .catch(() => null),
  applySuggestion: (thesisId, { chapterId, excerpt, replacement, position = null }) =>
    api
      .post(`/checks/${thesisId}/apply-suggestion`, {
        chapter_id: chapterId, excerpt, replacement, position,
      })
      .then((r) => r.data),
  /** PDF only: a review that can be edited is one that can be rewritten. */
  /**
   * A revised passage answering one observation, for the author to weigh.
   * Costs one model call; nothing is written until they accept it.
   */
  rewriteFinding: (thesisId, finding) =>
    api
      .post(`/checks/${thesisId}/rewrite`, finding, { timeout: 120000 })
      .then((r) => r.data),
  /** Rewrites and applies a batch of observations. One call per finding. */
  applyAdvice: (thesisId, findings) =>
    api
      .post(`/checks/${thesisId}/apply-advice`, { findings }, { timeout: 600000 })
      .then((r) => r.data),
  /** Mechanical corrections: what the document needs, and applying them. */
  tidyAvailable: (thesisId, chapterId = "") =>
    api.get(`/checks/${thesisId}/tidy`, { params: { chapter_id: chapterId } }).then((r) => r.data),
  tidy: (thesisId, fixes, chapterId = "", progressId = "") =>
    api
      .post(`/checks/${thesisId}/tidy`,
            { fixes, chapter_id: chapterId, progress_id: progressId }, { timeout: 600000 })
      .then((r) => r.data),
  /** A batch of accepted corrections, applied in one pass. */
  /** Heading levels and missing sections: changes to the document's shape. */
  applyStructure: (thesisId, fixes) =>
    api
      .post(`/checks/${thesisId}/structure`, { fixes }, { timeout: 120000 })
      .then((r) => r.data),

  applyAll: (thesisId, corrections, progressId = "") =>
    api
      .post(`/checks/${thesisId}/apply-all`, { corrections, progress_id: progressId },
            { timeout: 600000 })
      .then((r) => r.data),
  exportReview: (thesisId, groups, scopeTitle = "") =>
    api
      .post(`/theses/${thesisId}/review/export`, { groups, scope_title: scopeTitle },
            { responseType: "blob", timeout: 300000 })
      .then((r) => r.data),
  /** Model-backed spelling and grammar, asked for explicitly. */
  proofread: (thesisId, chapterId = "") =>
    api
      .post(`/checks/${thesisId}/proofread`, { chapter_id: chapterId }, { timeout: 300000 })
      .then((r) => r.data),
  stats: (thesisId, targetWords = 0) =>
    api.get(`/checks/${thesisId}/stats`, { params: { target_words: targetWords } }).then((r) => r.data),
};

export const bibliography = {
  styles: (thesisId) => api.get(`/checks/${thesisId}/bibliography/styles`).then((r) => r.data),
  list: (thesisId, style = "APA") =>
    api.get(`/checks/${thesisId}/bibliography`, { params: { style } }).then((r) => r.data),
  addDoi: (thesisId, doi, style = "APA") =>
    api.post(`/checks/${thesisId}/bibliography/doi`, { doi, style }).then((r) => r.data),
  /** Whole library, or one freshly uploaded source when `pdfId` is given. */
  enrich: (thesisId, pdfId = "") =>
    api
      .post(`/checks/${thesisId}/bibliography/enrich`, pdfId ? { pdf_id: pdfId } : null, {
        timeout: 300000,
      })
      .then((r) => r.data),
  /** Reads the whole reference list contained in one uploaded source. */
  fromSource: (thesisId, pdfId) =>
    api
      .post(`/checks/${thesisId}/bibliography/from-source`, { pdf_id: pdfId }, { timeout: 300000 })
      .then((r) => r.data),
  /** Converts a bibliography the author already wrote into structured records. */
  parseExisting: (thesisId, { file, text }) => {
    const fd = new FormData();
    if (file) fd.append("file", file);
    if (text) fd.append("text", text);
    return api
      .post(`/checks/${thesisId}/bibliography/parse`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      })
      .then((r) => r.data);
  },
  remove: (thesisId, recordId) =>
    api.delete(`/checks/${thesisId}/bibliography/${recordId}`).then((r) => r.data),
  importFile: (thesisId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post(`/checks/${thesisId}/bibliography/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
};

/* ---------------------------------------------------------------- numa */

export const numa = {
  action: (data) => api.post("/numa/action", data).then((r) => r.data),
  getInlineSuggestions: (text) => api.get("/numa/inline-suggestions", { params: { text } }).then((r) => r.data),
  getCitationSuggestions: ({ thesis_id, context, cursor_text = "" }) =>
    api.post("/numa/writing/citation-suggestions", { thesis_id, context, cursor_text }).then((r) => r.data),
  startingChat: ({ thesis_id, message, history = [], after_chapter_id = "", before_chapter_id = "" }) =>
    api
      .post("/numa/starting-chat", {
        thesis_id, message, history, after_chapter_id, before_chapter_id,
      })
      .then((r) => r.data),

  /** SSE stream. onError receives a normalised error object. */
  stream: async ({ action, text, chapter_title, project_type, venue, onDelta, onDone, onError, signal }) => {
    const token = readToken();
    const headers = { "Content-Type": "application/json", ...llmHeaders() };
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const res = await fetch(`${API}/numa/action/stream`, {
        method: "POST",
        headers,
        body: JSON.stringify({ action, text, chapter_title, project_type, venue }),
        signal,
      });

      if (!res.ok) {
        // Credentials are checked before the stream opens, so key problems
        // arrive as a normal status code with a structured body.
        let payload = null;
        try {
          payload = (await res.json())?.detail;
        } catch {
          /* non-JSON error body */
        }
        onError?.(normalizeError({ detail: payload || `HTTP ${res.status}` }));
        onDone?.();
        return;
      }
      if (!res.body) throw new Error("Risposta senza corpo");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const raw of lines) {
          if (!raw.startsWith("data: ")) continue;
          const payload = raw.slice(6);
          if (payload === "[DONE]") {
            onDone?.();
            return;
          }
          try {
            const parsed = JSON.parse(payload);
            if (parsed.delta) onDelta?.(parsed.delta);
            if (parsed.error) onError?.(normalizeError({ detail: parsed.error }));
          } catch {
            /* partial frame: wait for the rest */
          }
        }
      }
      onDone?.();
    } catch (e) {
      if (e?.name !== "AbortError") onError?.(normalizeError(e));
    }
  },
};

/* -------------------------------------------------------------- export */

export const exporter = {
  downloadUrl: (thesisId, format) => `${API}/theses/${thesisId}/export?format=${format}`,
  download: async (thesisId, format) => {
    const token = readToken();
    const res = await fetch(`${API}/theses/${thesisId}/export?format=${format}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Esportazione non riuscita (${res.status})`);
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="?([^"]+)"?/i);
    const filename = m ? m[1] : `documento.${format}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
